import { NavigateNext } from '@mui/icons-material'
import { Box, Breadcrumbs, CircularProgress, Link, Typography } from '@mui/material'
import { JSONContent } from '@tiptap/react'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DocPageDetailFragment, DocPageScope, useDocPageLazyQuery } from '../../generated'
import { Alert } from '../components/common/Alert'
import { DocPageEditor } from '../components/docs/DocPageEditor'
import { DocPageView } from '../components/docs/DocPageView'
import { DocsHome } from '../components/docs/DocsHome'
import { DocsTreeSidebar } from '../components/docs/DocsTreeSidebar'
import { RevisionHistoryDialog } from '../components/docs/RevisionHistoryDialog'
import { DOCS_LABELS, DOCS_MESSAGES, DOCS_ROUTE } from '../components/docs/docs.constants'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { AuthContext } from '../context'
import { useDocs } from '../hooks/useDocs'
import { DocReorderItem } from '../utils/docs'
import { DocTreeNode } from '../interfaceTypes/docs'

export const DocsContainer: FC = () => {
  const { isAdmin, isSuperAdmin, isAccountant } = useContext(AuthContext)
  const navigate = useNavigate()
  const { slug } = useParams()

  // Everyone can read the shared documentation; editing it is limited to admin and accounting.
  // `isAdmin` is already true for super-admins, but both are read for clarity. Mirrors the
  // API's DOCS_MANAGE permission — the server is the enforcement point, this only decides
  // which controls to show.
  const canEditOrgDocs = !!(isAdmin || isSuperAdmin || isAccountant)

  const [scope, setScope] = useState<DocPageScope>(DocPageScope.Org)
  // Personal pages are always managed by their owner; org pages need the editing roles.
  const canManage = scope === DocPageScope.Personal || canEditOrgDocs
  // Only the editing roles may flip a page between organization and private.
  const canSetVisibility = canEditOrgDocs

  const {
    tree,
    flatPages,
    refetch,
    createDocPage,
    updateDocPage,
    saveDraft,
    publishDocPage,
    publishing,
    archiveDocPage,
    unarchiveDocPage,
    deleteDocPage,
    reorderDocPages,
  } = useDocs(scope)

  const [page, setPage] = useState<DocPageDetailFragment | null>(null)
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [historyOpen, setHistoryOpen] = useState(false)

  const [fetchPage, { loading: pageLoading }] = useDocPageLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted: (data) => {
      if (data?.docPage?.docPage) setPage(data.docPage.docPage as DocPageDetailFragment)
    },
    onError: () => Alert.error(DOCS_MESSAGES.GENERIC_ERROR),
  })

  useEffect(() => {
    if (slug) fetchPage({ variables: { fetchDocPageInput: { slug } } })
    else setPage(null)
  }, [slug, fetchPage])

  const selectNode = (node: DocTreeNode) => {
    setMode('view')
    navigate(`${DOCS_ROUTE}/${node.slug}`)
  }

  const openSlug = (slugValue: string) => {
    setMode('view')
    navigate(`${DOCS_ROUTE}/${slugValue}`)
  }

  // Ancestor chain (root -> parent) for the current page, walked from the flat tree.
  const ancestors = useMemo(() => {
    if (!page) return []
    const byId = new Map(flatPages.map((p) => [p.id, p]))
    const chain: typeof flatPages = []
    const seen = new Set<string>()
    let cursor = page.parentId ? byId.get(page.parentId) : undefined
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id)
      chain.unshift(cursor)
      cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined
    }
    return chain
  }, [page, flatPages])

  const handleChangeVisibility = async (nextScope: DocPageScope) => {
    if (!page) return
    try {
      await updateDocPage({ variables: { updateDocPageInput: { id: page.id, scope: nextScope } } })
      // Follow the page into its new scope so the sidebar keeps it in view.
      setScope(nextScope)
      Alert.success(DOCS_MESSAGES.VISIBILITY_SUCCESS)
      await refetch()
      fetchPage({ variables: { fetchDocPageInput: { slug: page.slug } } })
    } catch {
      Alert.error(DOCS_MESSAGES.GENERIC_ERROR)
    }
  }

  const handleScopeChange = (next: DocPageScope) => {
    if (next === scope) return
    setScope(next)
    setMode('view')
    setPage(null)
    navigate(DOCS_ROUTE)
  }

  const handleReorder = async (items: DocReorderItem[]) => {
    if (!items.length) return
    try {
      await reorderDocPages({ variables: { reorderDocPagesInput: { items } } })
      await refetch()
    } catch {
      Alert.error(DOCS_MESSAGES.GENERIC_ERROR)
      await refetch()
    }
  }

  const handleCreate = async (parentId?: string) => {
    try {
      const { data } = await createDocPage({
        variables: { createDocPageInput: { title: DOCS_LABELS.UNTITLED, parentId, scope } },
      })
      const created = data?.createDocPage?.docPage
      await refetch()
      if (created) {
        setPage(created as DocPageDetailFragment)
        navigate(`${DOCS_ROUTE}/${created.slug}`)
        setMode('edit')
        Alert.success(DOCS_MESSAGES.CREATE_SUCCESS)
      }
    } catch {
      Alert.error(DOCS_MESSAGES.GENERIC_ERROR)
    }
  }

  const handleSaveDraft = async (title: string, content: JSONContent) => {
    if (!page) return
    await saveDraft({ variables: { saveDocPageDraftInput: { id: page.id, title, draftContent: content } } })
    refetch()
  }

  const handlePublish = async (title: string, content: JSONContent) => {
    if (!page) return
    try {
      await saveDraft({ variables: { saveDocPageDraftInput: { id: page.id, title, draftContent: content } } })
      await publishDocPage({ variables: { publishDocPageInput: { id: page.id, content } } })
      Alert.success(DOCS_MESSAGES.PUBLISH_SUCCESS)
      await refetch()
      setMode('view')
      fetchPage({ variables: { fetchDocPageInput: { slug: page.slug } } })
    } catch {
      Alert.error(DOCS_MESSAGES.GENERIC_ERROR)
    }
  }

  const handleArchive = async () => {
    if (!page) return
    try {
      await archiveDocPage({ variables: { id: page.id } })
      Alert.success(DOCS_MESSAGES.ARCHIVE_SUCCESS)
      await refetch()
      fetchPage({ variables: { fetchDocPageInput: { slug: page.slug } } })
    } catch {
      Alert.error(DOCS_MESSAGES.GENERIC_ERROR)
    }
  }

  const handleUnarchive = async () => {
    if (!page) return
    try {
      await unarchiveDocPage({ variables: { id: page.id } })
      Alert.success(DOCS_MESSAGES.UNARCHIVE_SUCCESS)
      await refetch()
      fetchPage({ variables: { fetchDocPageInput: { slug: page.slug } } })
    } catch {
      Alert.error(DOCS_MESSAGES.GENERIC_ERROR)
    }
  }

  const handleDelete = async () => {
    if (!page) return
    // eslint-disable-next-line no-alert
    if (!window.confirm(DOCS_MESSAGES.DELETE_CONFIRM)) return
    try {
      await deleteDocPage({ variables: { id: page.id } })
      Alert.success(DOCS_MESSAGES.DELETE_SUCCESS)
      setPage(null)
      await refetch()
      navigate(DOCS_ROUTE)
    } catch {
      Alert.error(DOCS_MESSAGES.GENERIC_ERROR)
    }
  }

  const renderContent = () => {
    // A slug is loading its page; show a spinner until it resolves.
    if (slug && pageLoading && !page) return <CircularProgress />
    // No page selected (or not found) -> the documentation home / search page.
    if (!page) return <DocsHome scope={scope} onOpen={openSlug} />
    if (mode === 'edit' && canManage)
      return (
        <DocPageEditor
          page={page}
          publishing={publishing}
          canSetVisibility={canSetVisibility}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onChangeVisibility={handleChangeVisibility}
          onDone={() => {
            setMode('view')
            fetchPage({ variables: { fetchDocPageInput: { slug: page.slug } } })
          }}
        />
      )
    return (
      <DocPageView
        page={page}
        canManage={canManage}
        onEdit={() => setMode('edit')}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onDelete={handleDelete}
        onHistory={() => setHistoryOpen(true)}
      />
    )
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      <Box sx={{ width: 300, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <DocsTreeSidebar
          nodes={tree}
          flatPages={flatPages}
          scope={scope}
          selectedId={page?.id}
          canManage={canManage}
          onScopeChange={handleScopeChange}
          onSelect={selectNode}
          onAddSubPage={(parentId) => handleCreate(parentId)}
          onAddRoot={() => handleCreate()}
          onReorder={handleReorder}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
        {page && (
          <Breadcrumbs
            separator={<NavigateNext fontSize="small" />}
            sx={{ maxWidth: 1040, mx: 'auto', mb: 2, '& .MuiBreadcrumbs-li': { minWidth: 0 } }}
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              variant="body2"
              onClick={() => navigate(DOCS_ROUTE)}
            >
              {DOCS_LABELS.HOME_CRUMB}
            </Link>
            {ancestors.map((ancestor) => (
              <Link
                key={ancestor.id}
                component="button"
                type="button"
                underline="hover"
                color="inherit"
                variant="body2"
                onClick={() => openSlug(ancestor.slug)}
                sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {ancestor.title || DOCS_LABELS.UNTITLED}
              </Link>
            ))}
            <Typography variant="body2" color="text.primary" noWrap sx={{ maxWidth: 240 }}>
              {page.title || DOCS_LABELS.UNTITLED}
            </Typography>
          </Breadcrumbs>
        )}
        {renderContent()}
      </Box>
      <RevisionHistoryDialog open={historyOpen} pageId={page?.id} onClose={() => setHistoryOpen(false)} />
    </Box>
  )
}
